import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';

const CODE_LEN = 6;

export type SessionVerifyStepProps = {
  email: string;
  code: string;
  setCode: (value: string) => void;
  sessionConsentChecked: boolean;
  setSessionConsentChecked: (checked: boolean) => void;
  onSubmit: () => void;
  onBack: () => void;
  onResend: () => void;
  isSubmitting: boolean;
  isRequestingCode: boolean;
  resendSeconds: number;
  errorMessage: string | null;
  hintCode: string | null;
};

export const SessionVerifyStep: React.FC<SessionVerifyStepProps> = ({
  email,
  code,
  setCode,
  sessionConsentChecked,
  setSessionConsentChecked,
  onSubmit,
  onBack,
  onResend,
  isSubmitting,
  isRequestingCode,
  resendSeconds,
  errorMessage,
  hintCode
}) => {
  const { t } = useTranslation();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const digits = React.useMemo(() => {
    const raw = code.replace(/\D/g, '').slice(0, CODE_LEN);
    return Array.from({ length: CODE_LEN }, (_, i) => raw[i] ?? '');
  }, [code]);

  const focusIndex = (i: number) => {
    const el = inputRefs.current[Math.max(0, Math.min(CODE_LEN - 1, i))];
    el?.focus();
    el?.select();
  };

  const mergeDigits = useCallback(
    (nextDigits: string[]) => {
      setCode(nextDigits.join('').replace(/\D/g, '').slice(0, CODE_LEN));
    },
    [setCode]
  );

  const handleDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    if (digit) {
      next[index] = digit;
      mergeDigits(next);
      if (index < CODE_LEN - 1) {
        requestAnimationFrame(() => focusIndex(index + 1));
      }
    } else {
      next[index] = '';
      mergeDigits(next);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      e.preventDefault();
      const next = [...digits];
      next[index - 1] = '';
      mergeDigits(next);
      focusIndex(index - 1);
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusIndex(index - 1);
    }
    if (e.key === 'ArrowRight' && index < CODE_LEN - 1) {
      e.preventDefault();
      focusIndex(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LEN);
    if (!pasted) return;
    setCode(pasted);
    requestAnimationFrame(() => focusIndex(Math.min(pasted.length, CODE_LEN - 1)));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!sessionConsentChecked) return;
    onSubmit();
  };

  return (
    <div className="sessionVerifyStep">
      <button type="button" className="sessionVerifyStep__back" onClick={onBack} aria-label={t('common.back')}>
        <ChevronLeft size={22} strokeWidth={2.25} />
      </button>

      <h2 className="sessionVerifyStep__title">{t('auth.verifyHeading')}</h2>
      <p className="sessionVerifyStep__sub">{t('auth.enterCode', { email })}</p>

      <form className="sessionVerifyStep__form" onSubmit={handleFormSubmit}>
        <div className="sessionVerifyStep__digits" onPaste={handlePaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => {
                inputRefs.current[i] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              name={i === 0 ? 'one-time-code' : undefined}
              maxLength={1}
              className="sessionVerifyStep__digit"
              value={d}
              aria-label={`${t('auth.codeLabel')} ${i + 1}`}
              onChange={(e) => handleDigitChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onFocus={(e) => e.target.select()}
            />
          ))}
        </div>

        <label className="sessionVerifyStep__consent">
          <input
            type="checkbox"
            checked={sessionConsentChecked}
            onChange={(e) => setSessionConsentChecked(e.target.checked)}
          />
          <span>
            {t('auth.acceptTerms')}{' '}
            <a href="/terms" target="_blank" rel="noreferrer">{t('auth.terms')}</a>{' '}
            {t('auth.and')}{' '}
            <a href="/privacy" target="_blank" rel="noreferrer">{t('auth.privacyPolicy')}</a>.
          </span>
        </label>

        <button
          type="submit"
          className="sessionVerifyStep__submit"
          disabled={code.replace(/\D/g, '').length < CODE_LEN || isSubmitting || !sessionConsentChecked}
        >
          {isSubmitting ? t('common.loading') : t('auth.verifyAndEnter')}
        </button>

        <div className="sessionVerifyStep__actions">
          <button
            type="button"
            className="sessionVerifyStep__link"
            onClick={onResend}
            disabled={isRequestingCode || resendSeconds > 0}
          >
            {resendSeconds > 0 ? t('auth.resendIn', { seconds: resendSeconds }) : t('auth.resendCode')}
          </button>
          <button type="button" className="sessionVerifyStep__link" onClick={onBack}>
            {t('auth.useDifferentEmail')}
          </button>
        </div>
      </form>

      {errorMessage && <div className="sessionVerifyStep__error">{errorMessage}</div>}
      {hintCode && <div className="sessionHint">Dev shortcut: code {hintCode}</div>}
    </div>
  );
};

export default SessionVerifyStep;
